import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { subscriptionApi } from '../services/api';
import { SubscriptionStatus, PlanOption } from '../types';
import { useAuth } from './AuthContext';

interface SubscriptionContextType {
  subscription: SubscriptionStatus | null;
  loading: boolean;
  isModalOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
  refreshSubscription: () => Promise<void>;
  checkSubscription: () => Promise<void>;
  daysRemaining: number;
  isActive: boolean;
  subscriptionPlan: string | null;
  pendingPlan: string | null;
  pendingPlanExpiresAt: string | null;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingPlan, setPendingPlan] = useState<string | null>(null);
  const [pendingPlanExpiresAt, setPendingPlanExpiresAt] = useState<string | null>(null);

  const refreshSubscription = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      setLoading(true);
      const res = await subscriptionApi.getStatus();
      if (res.success && res.data) {
        setSubscription(res.data);
        setPendingPlan(res.data.pendingPlan || null);
        setPendingPlanExpiresAt(res.data.pendingPlanExpiresAt || null);
      }
    } catch (err) {
      console.error('Abonelik verisi alınamadı:', err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      refreshSubscription();
    } else {
      setSubscription(null);
      setPendingPlan(null);
      setPendingPlanExpiresAt(null);
    }
  }, [isAuthenticated, refreshSubscription]);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const daysRemaining = subscription?.daysRemaining ?? 14;
  const isActive = subscription?.isActive ?? true;
  const subscriptionPlan = subscription?.plan ?? 'trial';

  return (
    <SubscriptionContext.Provider
      value={{
        subscription,
        loading,
        isModalOpen,
        openModal,
        closeModal,
        refreshSubscription,
        checkSubscription: refreshSubscription,
        daysRemaining,
        isActive,
        subscriptionPlan,
        pendingPlan,
        pendingPlanExpiresAt
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};
